/**
 * #2199 — the Canada switch, client half.
 *
 * `features.canada_intake_closed` (ConfigSettings, flipped by a TRFer through
 * PATCH /api/admin/config) keeps Canadian families on TRF's own process. The
 * server is the gate: it refuses a Canadian Region answer and a Canadian
 * submit on a draft with 403 `canada_intake_closed`
 * (backend/src/lib/canadaIntakeGate.js). This file lets the wizard say so
 * BEFORE the refusal, in place of the next step, instead of a "couldn't save"
 * toast.
 *
 * The switch is read off the public GET /api/applications/gate, which already
 * carries the other door rule (`application_codes_mode`). While loading, or if
 * the read fails, it reads OFF: the server still refuses, and the wizard turns
 * the refusal into the same notice (`isCanadaIntakeClosedError`).
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';

/** The server's refusal code (backend `CANADA_INTAKE_CLOSED_CODE`). */
export const CANADA_INTAKE_CLOSED_CODE = 'canada_intake_closed';

/** Its own key: the staff code-requirement panel caches `['applications', 'gate']`
 *  with a different failure rule (it shows the default mode). */
export const CANADA_INTAKE_QUERY_KEY = ['applications', 'gate', 'canada'] as const;

interface GateAnswer {
  mode?: string;
  canada_intake_closed?: boolean;
}

/** Whether TRF has closed the platform to Canadian families. False until known. */
export function useCanadaIntakeClosed(): boolean {
  const query = useQuery({
    queryKey: CANADA_INTAKE_QUERY_KEY,
    queryFn: async (): Promise<boolean> => {
      try {
        const answer = (await api.applications.gate()).data as GateAnswer | undefined;
        return answer?.canada_intake_closed === true;
      } catch {
        return false;
      }
    },
    staleTime: 10_000,
  });
  return query.data === true;
}

/** A 403 that says the Canada switch refused this save or submit — and only that one. */
export function isCanadaIntakeClosedError(err: unknown): boolean {
  const res = (err as { response?: { status?: number; data?: { code?: unknown } } })?.response;
  return res?.status === 403 && res.data?.code === CANADA_INTAKE_CLOSED_CODE;
}
